import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Eye, EyeOff, ShieldAlert} from "lucide-react";
import {formatDateTime} from "../../../shared/utils/format";
import {useTranslation, i18n} from "../../../shared/i18n";


const SCOPE = {
    owner: {get label() { return i18n.t("sections.onboarding.p132"); }, get secret() { return i18n.t("sections.onboarding.p133"); }},
    branch: {get label() { return i18n.t("sections.onboarding.p134"); }, get secret() { return i18n.t("sections.onboarding.p135"); }},
    applicant: {get label() { return i18n.t("sections.onboarding.p58"); }, get secret() { return i18n.t("sections.onboarding.p136"); }},
};

const focusableElements = (container) =>
    Array.from(
        container.querySelectorAll(
            "button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])",
        ),
    );

export const OneTimeSecretDialog = ({envelope, onClose}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const warningId = useId();
    const dialogRef = useRef(null);
    const acknowledgementRef = useRef(null);
    const invokingElementRef = useRef(null);
    const [revealed, setRevealed] = useState({});
    const [acknowledged, setAcknowledged] = useState(false);
    // Сброс revealed/acknowledged выполняется синхронно во время рендера при смене
    // конверта, а не в useEffect: отложенный эффект мог сработать уже после клика
    // пользователя по «Показать...» и затереть его выбор (гонка, ловившаяся тестами
    // под нагрузкой). Паттерн «derive state during render» из документации React.
    const [lastEnvelope, setLastEnvelope] = useState(envelope);
    if (envelope !== lastEnvelope) {
        setLastEnvelope(envelope);
        if (envelope) {
            setRevealed({});
            setAcknowledged(false);
        }
    }

    useEffect(() => {
        if (!envelope) return undefined;
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() => acknowledgementRef.current?.focus());

        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) {
                invokingElement.focus();
            } else {
                document.querySelector(`[aria-label='${t("sections.onboarding.p6")}']`)?.focus();
            }
            invokingElementRef.current = null;
        };
    }, [envelope]);

    useEffect(() => {
        if (!envelope) return undefined;
        const handleKeyDown = (event) => {
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [envelope]);

    if (!envelope) return null;

    return createPortal(
        <div className="onboarding-dialog-backdrop onboarding-secret-backdrop">
            <section
                ref={dialogRef}
                className="onboarding-dialog onboarding-secret-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={warningId}
            >
                <div className="onboarding-dialog-body">
                    <header className="onboarding-secret-header">
                        <div>
                            <p>{t("sections.onboarding.p137")} {formatDateTime(envelope.issuedAt)}</p>
                            <h2 id={titleId}>{envelope.title}</h2>
                        </div>
                        <ShieldAlert aria-hidden="true" size={22} />
                    </header>

                    <div
                        id={warningId}
                        className="onboarding-secret-warning"
                        role="alert"
                        aria-live="assertive"
                    >
                        {t("sections.onboarding.p138")}
                    </div>

                    <div className="onboarding-secret-list">
                        {envelope.values.map((value, index) => {
                            const key = `${value.scope}-${index}`;
                            const scope = SCOPE[value.scope];
                            const isRevealed = Boolean(revealed[key]);
                            const secretValue = value.password || value.token;
                            return (
                                <section key={key} className="onboarding-secret-row">
                                    <div className="onboarding-secret-row-heading">
                                        <div>
                                            <strong>{scope.label}</strong>
                                            {value.login ? <span>{value.login}</span> : null}
                                        </div>
                                        <button
                                            className="onboarding-icon-button"
                                            type="button"
                                            title={`${isRevealed ? t("sections.onboarding.p139") : t("sections.onboarding.p140")} ${scope.secret}`}
                                            aria-label={`${isRevealed ? t("sections.onboarding.p139") : t("sections.onboarding.p140")} ${scope.secret}`}
                                            onClick={() =>
                                                setRevealed((current) => ({
                                                    ...current,
                                                    [key]: !current[key],
                                                }))
                                            }
                                        >
                                            {isRevealed ? (
                                                <EyeOff aria-hidden="true" size={17} />
                                            ) : (
                                                <Eye aria-hidden="true" size={17} />
                                            )}
                                        </button>
                                    </div>
                                    {isRevealed ? (
                                        <code className="onboarding-secret-value">{secretValue}</code>
                                    ) : (
                                        <span
                                            className="onboarding-secret-mask"
                                            aria-label={t("sections.onboarding.p141")}
                                        >
                                            ••••••••••••
                                        </span>
                                    )}
                                </section>
                            );
                        })}
                    </div>

                    <label className="onboarding-secret-acknowledgement">
                        <input
                            ref={acknowledgementRef}
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(event) => setAcknowledged(event.target.checked)}
                        />
                        <span>{t("sections.onboarding.p142")}</span>
                    </label>
                </div>

                <footer className="onboarding-dialog-footer">
                    <button
                        className="admin-button admin-button-primary"
                        type="button"
                        disabled={!acknowledged}
                        onClick={() => {
                            setRevealed({});
                            setAcknowledged(false);
                            onClose();
                        }}
                    >
                        {t("sections.onboarding.p143")}
                    </button>
                </footer>
            </section>
        </div>,
        document.body,
    );
};
